import { acharCabecalho, CAMPO_CODIGO_DE_COMPRAS } from '../imports/cabecalho';
import { normalizeName } from '../shared/naming';

/**
 * A planilha de serviços da obra, lida como ela é.
 *
 * O orçamento que a construtora já usa vem numa pasta com **uma aba por tipologia** —
 * apartamento tipo 1 a 6, circulação, caixa de escada — e o "andar tipo", que é a soma
 * das outras. Dentro de cada aba, a hierarquia não está em colunas: está na **numeração
 * do item**.
 *
 * ```text
 * 3        ALVENARIA                    → disciplina
 * 3.1      ASSENTAMENTO DE ALVENARIA    → serviço, com unidade e quantidade (M², 508,85)
 * 3.1.1    TIJOLO CERAMICO 14X19        → item, com código de compras e quantidade
 * ```
 *
 * Ler a profundidade do número em vez de pedir uma coluna "nível" é o que faz a planilha
 * entrar como está, sem ninguém reformatá-la antes — e planilha que precisa ser preparada
 * para importar é planilha que não é importada.
 */

export interface ImportedItem {
  /** `"3.1.1"` — a posição na árvore, como a planilha escreve. */
  readonly number: string;
  /** `E-685110` no sistema de compras. Ausente na linha que ainda não foi codificada. */
  readonly code?: string;
  readonly description: string;
  readonly unit?: string;
  readonly quantity?: number;
  /** `PERMUTA` e afins — a modalidade com que aquele item é pago. */
  readonly payment?: string;
}

export interface ImportedService {
  readonly number: string;
  readonly name: string;
  readonly unit?: string;
  /** A quantidade do **serviço** — é dela que os itens abaixo derivam. */
  readonly quantity?: number;
  readonly items: readonly ImportedItem[];
}

export interface ImportedDiscipline {
  readonly number: string;
  readonly name: string;
  readonly services: readonly ImportedService[];
}

export interface ServiceSheet {
  /** O nome da aba — na planilha da obra, é o nome da tipologia. */
  readonly name: string;
  readonly disciplines: readonly ImportedDiscipline[];
}

/**
 * As colunas da numerada e os títulos com que o orçamento as escreve.
 *
 * Em qualquer posição, e com as variações que o reconhecedor comum entende (`imports/cabecalho.ts`):
 * "Quant.", "Und.", "Cód. compras", "Descrição do serviço". O código de compras é o campo comum
 * aos três leitores — é ele que dá preço ao material (Fase 29).
 */
export const CABECALHOS = {
  item: ['item'],
  code: CAMPO_CODIGO_DE_COMPRAS,
  payment: ['pagamento', 'forma de pagamento'],
  description: ['descricao', 'discriminacao', 'descricao do servico', 'descricao dos servicos'],
  unit: ['unidade', 'unidade de medida'],
  quantity: ['quantidade'],
} as const;

/** A profundidade do número: `3` = 1, `3.1` = 2, `3.1.1` = 3. Não-número devolve `undefined`. */
export function itemDepth(value: string): number | undefined {
  const texto = value.trim();
  if (texto === '' || !/^[\d.]+$/.test(texto)) return undefined;
  return texto.replace(/\.+$/, '').split('.').length;
}

/**
 * `"1.234,56"` e `"1234.56"` viram `1234.56`.
 *
 * A planilha vem de mãos diferentes e às vezes de exportações diferentes; recusar um dos
 * dois formatos faria a importação depender de quem salvou o arquivo.
 */
export function parseNumber(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const limpo = value.trim().replace(/\s/g, '');
  if (limpo === '') return undefined;

  const normalizado =
    limpo.includes(',') && limpo.lastIndexOf(',') > limpo.lastIndexOf('.')
      ? limpo.replace(/\./g, '').replace(',', '.')
      : limpo.replace(/,/g, '');

  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : undefined;
}

/** O cabeçalho é a linha com Item e Descrição — em qualquer linha da aba, como sempre foi. */
function cabecalhoDaAba(rows: readonly (readonly string[])[]) {
  return acharCabecalho(rows, CABECALHOS, ['item', 'description'], rows.length);
}

/**
 * Uma aba, virada em disciplina → serviço → item.
 *
 * Linha cuja numeração não é um número é ignorada em silêncio: título, rodapé e a linha em
 * branco entre blocos são parte de como planilha de obra é escrita, não erro a relatar.
 * Item que aparece **antes** de qualquer serviço também cai fora — ele não teria onde
 * pendurar, e adivinhar o pai seria inventar hierarquia.
 */
export function parseServiceSheet(
  name: string,
  rows: readonly (readonly string[])[],
): ServiceSheet {
  const cabecalho = cabecalhoDaAba(rows);
  if (cabecalho === undefined) return { name, disciplines: [] };
  const linhaCabecalho = cabecalho.linha;
  const colunas = cabecalho.colunas;

  const ler = (row: readonly string[], indice: number): string | undefined => {
    if (indice < 0) return undefined;
    const valor = row[indice]?.trim();
    return valor === undefined || valor === '' ? undefined : valor;
  };

  const disciplines: {
    number: string;
    name: string;
    services: {
      number: string;
      name: string;
      unit?: string;
      quantity?: number;
      items: ImportedItem[];
    }[];
  }[] = [];

  for (const row of rows.slice(linhaCabecalho + 1)) {
    const numero = ler(row, colunas.item);
    const profundidade = numero === undefined ? undefined : itemDepth(numero);
    const descricao = ler(row, colunas.description);
    if (numero === undefined || profundidade === undefined || descricao === undefined) continue;

    if (profundidade === 1) {
      disciplines.push({ number: numero, name: descricao, services: [] });
      continue;
    }

    const disciplina = disciplines[disciplines.length - 1];
    if (disciplina === undefined) continue;

    if (profundidade === 2) {
      disciplina.services.push({
        number: numero,
        name: descricao,
        items: [],
        ...(ler(row, colunas.unit) === undefined ? {} : { unit: ler(row, colunas.unit)! }),
        ...(parseNumber(ler(row, colunas.quantity)) === undefined
          ? {}
          : { quantity: parseNumber(ler(row, colunas.quantity))! }),
      });
      continue;
    }

    const servico = disciplina.services[disciplina.services.length - 1];
    if (servico === undefined) continue;

    servico.items.push({
      number: numero,
      description: descricao,
      ...(ler(row, colunas.code) === undefined ? {} : { code: ler(row, colunas.code)! }),
      ...(ler(row, colunas.unit) === undefined ? {} : { unit: ler(row, colunas.unit)! }),
      ...(ler(row, colunas.payment) === undefined ? {} : { payment: ler(row, colunas.payment)! }),
      ...(parseNumber(ler(row, colunas.quantity)) === undefined
        ? {}
        : { quantity: parseNumber(ler(row, colunas.quantity))! }),
    });
  }

  return { name, disciplines };
}

export interface SheetSummary {
  readonly disciplines: number;
  readonly services: number;
  readonly items: number;
  /** Códigos distintos do sistema de compras encontrados — o de-para que já existe. */
  readonly codes: number;
}

export function summarizeSheet(sheet: ServiceSheet): SheetSummary {
  const services = sheet.disciplines.flatMap((disciplina) => disciplina.services);
  const items = services.flatMap((servico) => servico.items);
  const codes = new Set(items.map((item) => item.code).filter((code) => code !== undefined));

  return {
    disciplines: sheet.disciplines.length,
    services: services.length,
    items: items.length,
    codes: codes.size,
  };
}

/** `"12 disciplinas · 40 serviços · 218 itens · 78 códigos de compras"`. */
export function describeSheet(summary: SheetSummary): string {
  const parte = (quantos: number, um: string, muitos: string): string =>
    `${String(quantos)} ${quantos === 1 ? um : muitos}`;

  return [
    parte(summary.disciplines, 'disciplina', 'disciplinas'),
    parte(summary.services, 'serviço', 'serviços'),
    parte(summary.items, 'item', 'itens'),
    parte(summary.codes, 'código de compras', 'códigos de compras'),
  ].join(' · ');
}

/**
 * As abas que são tipologia, separadas da que é soma.
 *
 * O "andar tipo" da planilha é a soma das outras — importá-lo junto contaria o prédio duas
 * vezes. Ele continua útil como **conferência**: se a soma das tipologias não bater com
 * ele, alguma aba ficou de fora ou entrou torta.
 */
export function isTotalSheet(name: string): boolean {
  const normalizado = normalizeName(name);
  return normalizado.includes('andar tipo') || normalizado.includes('pavimento tipo');
}
